import {WBIcon} from '@wandb/ui';
import {Button} from '@wandb/weave/components/Button';
import classNames from 'classnames';
import _ from 'lodash';
import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {Popup} from 'semantic-ui-react';

import * as DropdownMenu from '../../components/DropdownMenu';
import {useProjectPageQuery} from '../../generated/graphql';
import {MOBILE_WIDTH} from '../../pages/FC.styles';
import {
  trackPanelSearchCleared,
  trackPanelSearchHistoryItemSelected,
  trackPanelSearchHistoryOpened,
  trackWorkspaceSettingsOpened,
} from '../../services/analytics';
import {isInJupyterNotebook} from '../../setup';
import {
  PanelBankContext,
  PanelBankUpdaterContext,
} from '../../state/panelbank/context';
import {
  usePanelSearchDebouncedContext,
  usePanelSearchInputContext,
} from '../../state/panelbank/PanelSearchContextProvider';
import {useViewer} from '../../state/viewer/hooks';
import * as CustomRunColorsViewTypes from '../../state/views/customRunColors/types';
import * as ViewHooks from '../../state/views/hooks';
import * as PanelBankConfigActions from '../../state/views/panelBankConfig/actions';
import * as PanelBankConfigTypes from '../../state/views/panelBankConfig/types';
import {PanelBankSectionConfig} from '../../state/views/panelBankSectionConfig/types';
import * as PanelSettingsTypes from '../../state/views/panelSettings/types';
import * as RunSetViewTypes from '../../state/views/runSet/types';
import {RunHistoryKeyInfo} from '../../types/run';
import {panelIsActive, searchRegexFromQuery} from '../../util/panelbank';
import {getFlowSectionsWithVisiblePanels} from '../../util/panelbankFlow';
import {LayedOutPanel} from '../../util/panels';
import * as RunHelpers from '../../util/runhelpers';
import {
  CREATE_REPORT_FLASHER_FEATURE_KEY,
  useOnboardingContext,
  USER_TOGGLED_TABLE_VIEW_FEATURE_KEY,
} from '../../util/userContext';
import {useWindowSize} from '../../util/window';
import {CreateReportModal} from '../CreateReportModal';
import {PersistentFlasher} from '../elements/PersistentFlasher';
import {LinePlotPanel, PanelSettingsComponent} from '../PanelSettings';
import {CreateReportTooltipPrompt} from '../Reports';
import {Tailwind} from '../Tailwind';
import * as PanelRunsLinePlot from './../PanelRunsLinePlot';
import {PanelBankControlsModal} from './PanelBankControlsModal';
import {PanelBankSearchHistoryItem} from './types';
import {PanelBankConfigWithRefs} from './usePanelBankConfigWithRefs';

interface PanelBankControlsProps {
  entityName: string;
  projectName: string;
  readOnly?: boolean;
  panelSettingsRef: PanelSettingsTypes.Ref;
  panelBankConfigRef: PanelBankConfigTypes.Ref;
  panelBankConfigWithRefs: PanelBankConfigWithRefs;
  runSetRefs: RunSetViewTypes.Ref[];
  customRunColorsRef: CustomRunColorsViewTypes.Ref;
  singleRun?: boolean;
  historyKeyInfo: RunHistoryKeyInfo;
  panelBankWidth: number;
  workspaceID?: string;
  resetAllLocals(
    localSettings: PanelSettingsTypes.PanelSettings,
    workspaceSettings: PanelSettingsTypes.PanelSettings
  ): void;
}

// The input box used for searching panels
export const PanelSearchInput: React.FC<{isSearchBetaEnabled: boolean}> = ({
  isSearchBetaEnabled,
}) => {
  const {isSearching} = usePanelSearchDebouncedContext();
  const {
    searchInputCallbackRef,
    searchQuery,
    onChange,
    onAddToHistory,
    searchHistory,
  } = usePanelSearchInputContext();
  const [historyHoverText, setHistoryHoverText] = useState<string>();

  return (
    <Tailwind style={{width: '100%'}}>
      <div
        className={classNames(
          'flex w-full rounded border-moon-700 bg-white focus-within:border focus-within:border-teal-450',
          {
            'outline outline-4 outline-gold-300':
              isSearching && isSearchBetaEnabled,
          }
        )}>
        <WBIcon
          name="search"
          className="w-32 translate-x-1 translate-y-4 text-center text-[20px] text-moon-450"
        />
        <input
          type="text"
          ref={searchInputCallbackRef}
          className={classNames('grow focus:outline-0', {
            'text-moon-450': historyHoverText != null,
          })}
          tabIndex={0}
          value={historyHoverText ?? searchQuery}
          placeholder="Search panels with regex"
          onChange={e => onChange(e.target.value)}
          onBlur={() => {
            // when to add to search history is a bit tricky - we chose blur since
            // it is a reasonable proxy for "the user finished editing the search",
            // and trying to save after X seconds of pause could be wrong if a
            // user stopped to think of something while typing a search.
            onAddToHistory(searchQuery);
          }}
          aria-label="search panels with regex"
          data-test="panel-search-input"
        />
        <Button
          variant="ghost"
          icon="close"
          className={
            'rounded-none text-moon-450' /* turn off rounding so hover state looks nice in the parent container*/
          }
          onClick={() => {
            trackPanelSearchCleared();
            onChange('');
          }}
          aria-label={'clear search'}
          tooltip="Clear search"
        />
        <PanelBankHistory
          searchHistory={searchHistory}
          onItemSelected={val => {
            // we need this to save to history since the change
            onChange(val);
            onAddToHistory(val);
          }}
          setHistoryHoverText={setHistoryHoverText}
        />
      </div>
    </Tailwind>
  );
};

export const PanelBankHistory: FC<{
  searchHistory: PanelBankSearchHistoryItem[];
  onItemSelected: (val: string) => void;
  setHistoryHoverText: (val: string | undefined) => void;
}> = ({searchHistory, onItemSelected, setHistoryHoverText}) => {
  const [isOpen, setIsOpen] = useState(false);
  const onOpenChange = (open: boolean) => {
    setIsOpen(open);
    setHistoryHoverText(undefined);
    if (open) {
      trackPanelSearchHistoryOpened();
    }
  };

  const {width: screenWidth} = useWindowSize();
  const isOnLargeScreen = screenWidth > MOBILE_WIDTH;

  return (
    <DropdownMenu.Root modal={false} onOpenChange={onOpenChange} open={isOpen}>
      <DropdownMenu.Trigger>
        <Button
          variant="ghost"
          icon="history"
          className="text-moon-450"
          active={isOpen}
          aria-label="search history"
          disabled={searchHistory.length === 0}
          tooltip="View Search History"
        />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className={classNames(
            'z-[5000]',
            // for now we are restricting the width (and truncating text below), so this
            // works on mobile as well. Note that since the user hovering over a menu item
            // shows ghost text of the hovered item in the input selector, we don't have to worry
            // too much about the fact that users can't see the full menu item
            'max-w-xs sm:max-w-screen-sm'
          )}
          align="end"
          collisionPadding={5}>
          {searchHistory.map((i, index) => (
            <DropdownMenu.Item
              key={index}
              onClick={() => {
                onItemSelected(i.query);
                trackPanelSearchHistoryItemSelected({index});
              }}
              onMouseOver={() => setHistoryHoverText(i.query)}
              onFocus={() => setHistoryHoverText(i.query)}>
              {RunHelpers.truncateString(i.query, isOnLargeScreen ? 80 : 35)}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
};
// This is the bar at the top of the PanelBank that contains search and settings
const PanelBankControls = (props: PanelBankControlsProps) => {
  const {
    entityName,
    projectName,
    readOnly,
    panelBankConfigRef,
    panelBankConfigWithRefs,
    panelSettingsRef,
    runSetRefs,
    customRunColorsRef,
    singleRun,
    historyKeyInfo,
    panelBankWidth,
    workspaceID,
    resetAllLocals,
  } = props;

  const viewer = useViewer();

  const [isModalOpen, setIsModalOpen] = React.useState<boolean>(false);

  const updatePanelBankSettings = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.updateSettings
  );

  const {currentPageBySectionRefID} = useContext(PanelBankContext);
  const {setExportingUnknownID} = useContext(PanelBankUpdaterContext);
  const setExporting = useCallback(
    () => setExportingUnknownID(true),
    [setExportingUnknownID]
  );

  const {debouncedSearchQuery} = usePanelSearchDebouncedContext();

  const useExportableSections = () => {
    const {sections} = ViewHooks.useWhole(panelBankConfigRef);
    return useMemo(
      () =>
        getFlowSectionsWithVisiblePanels({
          sections,
          currentPageBySectionRefID,
          singleRun,
          debouncedSearchQuery,
          historyKeyInfo,
          panelBankWidth,
        }),
      [sections]
    );
  };

  /**
   * Note(jofang): Purposefully leaving this commented out code in while I
   * wait for product and design to decide on the direction of this use case.
   */
  // const pushDraftReport = usePushReport(entityName, projectName);

  const useAllLinePlotPanels = () => {
    const {sections} = ViewHooks.useWhole(panelBankConfigRef);
    return useMemo(
      () =>
        _.flatten(
          sections.map(s =>
            s.panels.filter(p => p.viewType === PanelRunsLinePlot.PANEL_TYPE)
          )
        ) as unknown as LinePlotPanel[],
      [sections]
    );
  };

  const keyTypes = RunHelpers.useKeyTypes(historyKeyInfo);
  const searchRegex = searchRegexFromQuery(debouncedSearchQuery);
  const usePanelIsActive = useCallback(
    (section: PanelBankSectionConfig, panel: LayedOutPanel) =>
      panelIsActive({
        section,
        panel,
        singleRun,
        searchRegex,
        historyKeyInfo,
        keyTypes,
      }),
    [singleRun, searchRegex, historyKeyInfo, keyTypes]
  );

  const [workspaceSettingsLabelPopupOpen, setWorkspaceSettingsLabelPopupOpen] =
    useState(false);

  return (
    <div className="panel-bank__actions-wrapper">
      <div className="panel-bank__actions">
        <PanelSearchInput isSearchBetaEnabled={false} />
        <PanelSettingsComponent
          runSetRefs={runSetRefs}
          workspacePanelSettingsRef={panelSettingsRef}
          useAllLinePlotPanels={useAllLinePlotPanels}
          resetAllLocals={resetAllLocals}
        />
        {workspaceID != null && (
          <Popup
            content="Workspace settings"
            open={workspaceSettingsLabelPopupOpen}
            onClose={() => setWorkspaceSettingsLabelPopupOpen(false)}
            onOpen={() => {
              if (!isModalOpen) {
                setWorkspaceSettingsLabelPopupOpen(true);
              }
            }}
            position="bottom right"
            trigger={
              <Button
                style={{marginLeft: '8px'}}
                icon="settings"
                variant="ghost"
                onClick={() => {
                  trackWorkspaceSettingsOpened();
                  setWorkspaceSettingsLabelPopupOpen(false);
                  setIsModalOpen(true);
                }}
              />
            }
          />
        )}
        {workspaceID != null && (
          <PanelBankControlsModal
            panelBankConfigRef={panelBankConfigRef}
            panelBankConfigWithRefs={panelBankConfigWithRefs}
            workspaceID={workspaceID}
            isModalOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
            }}
            updatePanelBankSettings={updatePanelBankSettings}
          />
        )}
        {!readOnly && viewer != null && (
          <>
            {!isInJupyterNotebook() && (
              <CreateReportButton
                disabled={false}
                entityName={entityName}
                projectName={projectName}
                onClick={() => {
                  window.analytics?.track('Create Report Started', {
                    location: `workspace create report button`,
                    entityName,
                    projectName,
                  });

                  setExporting();

                  /**
                   * Note(jofang): Purposefully leaving this commented out code in while I
                   * wait for product and design to decide on the direction of this use case.
                   */
                  // const report = Report.getEmptyReportConfig();
                  // pushDraftReport(report, undefined, {
                  //   newTab: true,
                  // });
                }}
              />
            )}
            <CreateReportModal
              useExportableSections={useExportableSections}
              panelSettingsRef={panelSettingsRef}
              runSetRefs={runSetRefs}
              customRunColorsRef={customRunColorsRef}
              panelIsActive={usePanelIsActive}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default PanelBankControls;

const CreateReportButton: React.FC<{
  disabled: boolean;
  entityName: string;
  projectName: string;
  onClick(): void;
}> = ({disabled, entityName, projectName, onClick}) => {
  const projectQuery = useProjectPageQuery({
    variables: {
      entityName,
      projectName,
    },
  });
  const runCount = projectQuery.data?.project?.runCount;

  const {onboardingState, setOnboardingState, loading} = useOnboardingContext(
    CREATE_REPORT_FLASHER_FEATURE_KEY
  );
  const {onboardingState: tableViewState, loading: tableViewLoading} =
    useOnboardingContext(USER_TOGGLED_TABLE_VIEW_FEATURE_KEY);

  // Show the flashing blue dot if there are more than 20 runs
  // and the user hasn't yet clicked this button to dismiss createReport onboarding
  // and the 'toggle table view' flasher isn't present.
  const tableViewToggled =
    !tableViewLoading && !!tableViewState.toggledTableView;
  const flasherEnabled =
    !loading &&
    !onboardingState.createReportFlasher &&
    tableViewToggled &&
    runCount != null &&
    runCount > 20;

  useEffect(() => {
    if (flasherEnabled) {
      window.analytics?.track('Flasher Viewed', {
        entityName,
        projectName,
        location: 'create report button',
      });
    }
  }, [flasherEnabled, entityName, projectName]);

  const onCreateReportClick = () => {
    onClick();
    if (flasherEnabled) {
      window.analytics?.track('Flasher Dismissed', {
        entityName,
        projectName,
        location: 'create report button',
      });
    }
    setOnboardingState({...onboardingState, createReportFlasher: true});
  };
  return (
    <PersistentFlasher
      visible={flasherEnabled}
      offsetX={-69}
      style={{zIndex: 99}}
      trigger={
        <Button
          className="save-snapshot-button"
          variant="primary"
          onClick={onCreateReportClick}
          disabled={disabled}>
          Create report
        </Button>
      }
      popupClassName={`create-report-prompt-tooltip`}
      popupPosition={`bottom right`}
      popupOffsetX={13}
      popupContent={
        <CreateReportTooltipPrompt
          location={`workspace`}
          entityName={entityName}
          projectName={projectName}
        />
      }
    />
  );
};
